import { connect } from 'react-redux'
import { reduxForm } from 'redux-form'
import { Dispatch } from 'redux'
import mapObjectToFormSettings from '../../../models/Object/mappers/mapObjectToFormSettings'
import { getUserSettings } from '../../../reducers/userObject/selectors/settings'
import { toggleServicesModal } from '../../../actions/bookingCalendar/reservationModal/toggleServicesModal'
import { toggleProvenancesModal } from '../../../actions/provenances/toggleProvenancesModal'
import { addProvenance } from '../../../actions/provenances/addProvenance'
import changeChildrenCount from '../../../actions/bookingCalendar/reservationModal/changeChildrenCount'
import { changeReservationDate } from '../../../actions/bookingCalendar/reservationModal/changeReservationDate'
import { calculateEndPrice } from '../../../actions/bookingCalendar/reservationModal/calculateEndPrice'
import {
  getFromGroupReservations,
  getUpdateReservationFormEvent,
} from '../../../reducers/bookingCalendar/selectors/events'
import mapEventToFormValues from '../../../models/Event/mappers/mapEventToFormValues'
import onReservationFormChange from '../../../actions/bookingCalendar/reservationModal/onReservationFormChange'
import {
  getReservationFormVeryCurrentTab,
  getUpdateReservationFormTotalAdultsCount,
} from '../../../reducers/bookingCalendar/selectors/reservationForm'
import selectReservationTab from '../../../actions/bookingCalendar/reservationModal/selectReservationTab'
import {
  getActiveLicense,
  getInvoiceLicense,
  isLicenseValid,
} from '../../../reducers/userAccount/selectors/license'
import { getModalReservationIsSaving } from '../../../reducers/bookingCalendar/selectors/getModalOpen'
import { mapItemToPrice } from '../../../utils/Money'
import {
  ICreateReservationDispatch,
  ICreateReservationFormValues,
  ICreateReservationProps,
} from './CreateReservation'
import { onSubmitUpdate } from './submit'
import validate, { shouldAsyncValidate } from './validate'
import { FormNames } from './index'
import { ModalActions } from '../../../reducers/modals/actionNames'
import { fireToastError } from '../../../actions/toasts/fireToast'
import i18n from '../../../i18n/i18n'
import { isInScope } from '../../../hooks/useIsScope'
import { PermissionScope } from '../../Employees'
import { Mixpanel } from '../../../utils/Mixpanel'
import { isUserNewPackage } from '../../../reducers/userAccount/selectors/userAccount'

export default function(formName: string) {
  return reduxForm({
    asyncValidate: validate(),
    destroyOnUnmount: true,
    enableReinitialize: false,
    form: formName,
    onChange: (nextValues, dispatch, props, prevValues) => {
      dispatch(
        onReservationFormChange(nextValues, prevValues, FormNames.Update),
      )
    },
    onSubmit: onSubmitUpdate(),
    shouldAsyncValidate: shouldAsyncValidate,
    updateUnregisteredFields: true,
  })
}

const mapStateToProps = () => {
  return (state): ICreateReservationProps => {
    const rawEvent = getUpdateReservationFormEvent(state)

    const groupReservations = getFromGroupReservations(state)

    const event: ICreateReservationFormValues = mapEventToFormValues(rawEvent)

    const userValues = mapObjectToFormSettings(getUserSettings(state))

    const scope = isInScope(state)

    return {
      allowConfig: scope([PermissionScope.SCOPE_CAN_USE_PRICING_SETTINGS]),
      allowFvat: scope([PermissionScope.SCOPE_CAN_USE_INVOICES]),
      allowTemplates: scope([PermissionScope.SCOPE_CAN_USE_TEMPLATES]),
      allowReservationEdit: scope([
        PermissionScope.SCOPE_CAN_EDIT_RESERVATIONS,
      ]),
      hideUserData: scope([PermissionScope.SCOPE_HIDE_CLIENT_DATA]),
      id: rawEvent.id,
      booking: rawEvent.bookingId,
      airbnb: rawEvent.airbnbId,
      price: rawEvent.price,
      fromWebsite: String(rawEvent.from_website),
      groupReservations: groupReservations,
      bookingPrice: mapItemToPrice(rawEvent.bookingPrice).toFormat(),
      formatDate: userValues.dateFormat,
      licenseValid: isLicenseValid(state),
      licenseType: getActiveLicense(state),
      isNewPremiumPriceGroup: isUserNewPackage(state),
      invoice: getInvoiceLicense(state),
      iCalendar: rawEvent.iCalendarId,
      initialValues: {
        ...event,
        pricePerPerson: (
          Number(event.endPrice) /
          Number(event.daysCount) /
          (Number(event.adultsCount) + Number(event.childrenCount))
        ).toFixed(2),
      },
      isSaving: getModalReservationIsSaving(state),
      premium: isLicenseValid(state),
      roomSize: getUpdateReservationFormTotalAdultsCount(state),
      selectedTab: getReservationFormVeryCurrentTab(state),
      timeFrom: userValues.daynightFrom,
      timeTo: userValues.daynightTo,
      type: 'update',
    }
  }
}

const mapDispatchToProps = (
  dispatch: Dispatch,
  ownProps,
): ICreateReservationDispatch => ({
  addProvenance: (provenance: string) => dispatch(addProvenance(provenance)),
  calculate: () => dispatch(calculateEndPrice(FormNames.Update)),
  changeChildren: (count: number) =>
    dispatch(changeChildrenCount(count, FormNames.Update)),
  changeDates: (name: string, e: Date | number) =>
    dispatch(changeReservationDate(name, e, FormNames.Update)),
  refresh: () => null,
  closeTooltips: () => dispatch({ type: ModalActions.CloseTooltips }),
  selectTab: index => dispatch(selectReservationTab(index)),
  toggleProvenancesModal: (toggle?: boolean) => () =>
    dispatch(toggleProvenancesModal(toggle)),
  toggleServicesModal: (toggle?: boolean) => () =>
    dispatch(toggleServicesModal(toggle)),
  toggleHideModal: (toggle?: boolean) => {
    dispatch({ type: ModalActions.ToggleHideModal, payload: toggle })
  },
  firePricingError: () => {
    Mixpanel.track('Employee Access Denied', {
      'Employee ID': ownProps.workerId,
    })
    dispatch(fireToastError(i18n.t('BED_3621_no_permission')))
  },
})

export const withUpdateInitialValues = connect(
  mapStateToProps(),
  mapDispatchToProps,
)
